import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentPropsWithoutRef } from "react";

import { cn } from "@/lib/cn";
import { INTERACTIVE } from "@/lib/interaction";

const button = cva(
  `inline-flex shrink-0 items-center justify-center gap-1.5 font-semibold whitespace-nowrap disabled:pointer-events-none disabled:opacity-45 ${INTERACTIVE}`,
  {
    variants: {
      variant: {
        /** 주 동작 — 시작하기, 보내기, 채팅 시작 */
        primary: "bg-ink text-paper hover:bg-ink/88",
        /** 밝은 면 위의 보조 동작 */
        secondary: "bg-paper text-ink hover:bg-chip",
        /** 다크 레일 위의 동작 — 새 채팅 */
        onDark: "bg-paper/12 text-paper hover:bg-paper/20",
        /** 테두리만 있는 것 — 대화 목록으로 옮기기 */
        outline: "border-line-strong text-body hover:bg-paper border",
        /** 배경 없는 것 — 되돌리기, 모두 읽음 */
        ghost: "text-body hover:bg-paper/60",
        /** 파괴적 동작 — 차단하기, 로그아웃 */
        danger: "text-danger hover:bg-danger/8",
      },
      size: {
        xs: "h-[34px] rounded-[9px] px-2.5 text-[13px]",
        sm: "h-9 rounded-[10px] px-3.5 text-[13px]",
        md: "h-12 rounded-xl px-4 text-[15px]",
        lg: "h-13 rounded-[14px] px-6.5 text-[15px]",
        xl: "h-[58px] rounded-xl px-5 text-[17px]",
        /** 알약 — 대화 목록으로 옮기기 */
        pill: "h-[34px] rounded-[20px] px-3.5 text-[13px] font-normal",
      },
      block: {
        true: "w-full",
        false: "",
      },
    },
    defaultVariants: { variant: "primary", size: "md", block: false },
  },
);

type ButtonProps = VariantProps<typeof button> & {
  /** true면 자식 엘리먼트에 스타일만 입힌다. 링크를 버튼처럼 보일 때 쓴다. */
  asChild?: boolean;
} & ComponentPropsWithoutRef<"button">;

export function Button({
  variant,
  size,
  block,
  asChild = false,
  className,
  type = "button",
  ...rest
}: ButtonProps) {
  const Component = asChild ? Slot : "button";

  return (
    <Component
      className={cn(button({ variant, size, block }), className)}
      {...(asChild ? {} : { type })}
      {...rest}
    />
  );
}
